import { useCallback, useMemo } from "react";

import { Box, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { type EChartsOption } from "echarts";
import ECharts from "echarts-for-react";

import { useDarkThemeCheck } from "../../../../../Components/hooks/useDarkThemeCheck";
import { type K8sUtilizationMetric } from "../../../types";
import { ChartId } from "../ClusterDetail";
import { TimeRange } from "../Components/TimeRangeSelector";
import { useClusterDetailDashboardContext } from "../context";
import ChartCard from "./ChartCard";
import {
  formatCpu,
  formatMemory,
  formatTimestamp,
  formatTimestampAxis,
  formatTimestampHourAxis,
  getCommonChartOptions,
} from "./utils";

const areaStyle = {
  opacity: 0.5,
};
const xAxisIntervals = 4;

const chartStyles: React.CSSProperties = {
  height: "100%",
  width: "100%",
};

interface UtilizationChartsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getEventHandlers?: (chartId: ChartId) => Record<string, (event: any) => void>;
  metrics: K8sUtilizationMetric[];
  loading?: boolean;
  memoryChartRef?: React.RefObject<ECharts>;
  cpuChartRef?: React.RefObject<ECharts>;
}

const UtilizationCharts = ({
  metrics: originalMetrics,
  loading,
  getEventHandlers,
  memoryChartRef,
  cpuChartRef,
}: UtilizationChartsProps) => {
  const theme = useTheme();
  const isDarkMode = useDarkThemeCheck();
  const { timeRange } = useClusterDetailDashboardContext();

  const isDayGranularity = [TimeRange.T_14D, TimeRange.T_30D].includes(timeRange ?? TimeRange.T_30D);
  const isHourlyTimeRange = [TimeRange.T_1H, TimeRange.T_4H, TimeRange.T_12H, TimeRange.T_1D].includes(
    timeRange ?? TimeRange.T_30D
  );

  const xAxisLabelFormatter = useCallback(
    (value: string) => (isHourlyTimeRange ? formatTimestampHourAxis(value) : formatTimestampAxis(value)),
    [isHourlyTimeRange]
  );

  const metrics = useMemo(
    () =>
      originalMetrics.map((m) => ({
        ...m,
        formatted_start_time: isDayGranularity
          ? formatTimestampAxis(formatTimestamp(m.start_time))
          : formatTimestamp(m.start_time),
      })),
    [originalMetrics, isDayGranularity]
  );

  const isEmpty = metrics.length === 0;
  const showSymbol = metrics.length <= 10;

  const cpuChartOption: EChartsOption = {
    ...getCommonChartOptions(theme, {
      tooltip: {
        valueFormatter(value) {
          return formatCpu(String(value), 2);
        },
      },
      yAxis: {
        axisLabel: {
          formatter: (value) => formatCpu(String(value), 2),
        },
      },
    }),
    xAxis: {
      data: metrics.map((m) => m.formatted_start_time),
      axisLabel: {
        formatter: xAxisLabelFormatter,
        interval: Math.floor(metrics.length / xAxisIntervals),
      },
    },
    series: [
      {
        name: "Allocated",
        type: "line",
        data: metrics.map((m) => m.cpu_limit),
        color: isDarkMode ? "#FFFFFF" : "#707070",
        showSymbol,
        areaStyle,
      },
      {
        name: "Requested",
        type: "line",
        data: metrics.map((m) => m.cpu_requested),
        color: isDarkMode ? "#1AB4FF" : "#0090d6",
        showSymbol,
        areaStyle,
      },
      {
        name: "Used",
        type: "line",
        data: metrics.map((m) => m.cpu_use),
        color: isDarkMode ? "#B2E9F0" : "#94E1EB",
        showSymbol,
        areaStyle,
      },
    ],
  };

  const memoryChartOption: EChartsOption = {
    ...getCommonChartOptions(theme, {
      tooltip: {
        valueFormatter(value) {
          return value ? formatMemory(String(value)) : "0";
        },
      },
      yAxis: {
        axisLabel: {
          formatter: (v) => formatMemory(v, 0),
        },
      },
    }),
    xAxis: {
      data: metrics.map((m) => m.formatted_start_time),
      axisLabel: {
        formatter: xAxisLabelFormatter,
        interval: Math.floor(metrics.length / xAxisIntervals),
      },
    },
    series: [
      {
        name: "Allocated",
        type: "line",
        data: metrics.map((m) => m.mem_limit),
        color: isDarkMode ? "#FFFFFF" : "#707070",
        showSymbol,
        areaStyle,
      },
      {
        name: "Requested",
        type: "line",
        data: metrics.map((m) => m.mem_requested),
        color: isDarkMode ? "#8D24A8" : "#461264",
        showSymbol,
        areaStyle,
      },
      {
        name: "Used",
        type: "line",
        data: metrics.map((m) => m.mem_use),
        color: "#E4A3F5",
        showSymbol,
        areaStyle,
      },
    ],
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <ChartCard title="Cluster Memory utilization" loading={loading} empty={isEmpty}>
            <ECharts
              style={chartStyles}
              option={memoryChartOption}
              ref={memoryChartRef}
              onEvents={getEventHandlers?.(ChartId.MEMORY)}
              lazyUpdate
            />
          </ChartCard>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <ChartCard title="Cluster CPU utilization" loading={loading} empty={isEmpty}>
            <ECharts
              style={chartStyles}
              option={cpuChartOption}
              ref={cpuChartRef}
              onEvents={getEventHandlers?.(ChartId.CPU)}
              lazyUpdate
            />
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UtilizationCharts;
